import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

const RegisterForm = ({ onModeChange }) => {
  const { t } = useTranslation();
  const { signUp, resendConfirmationEmail, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Effacer les erreurs lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = t('validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('validation.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('validation.passwordTooShort');
    } else if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = t('auth.passwordRequirements');
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = t('terms.acceptTermsRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Générer le display_name à partir de l'email
      const displayName = formData.email.split('@')[0];
      
      // Préparation des données utilisateur pour Supabase
      const userData = {
        display_name: displayName,
        full_name: displayName,
        avatar_url: '',
        email_verified: false
      };

      const result = await signUp(formData.email, formData.password, userData);
      
      if (result?.success) {
        setSuccessMessage(t('auth.checkEmailConfirmation'));
        setRegisteredEmail(formData.email);
      }
    }
  };

  const handleResendConfirmation = async () => {
    if (!registeredEmail) return;
    
    setResendLoading(true);
    setResendSuccess(false);
    
    try {
      const result = await resendConfirmationEmail(registeredEmail);
      if (result?.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Erreur lors du renvoi de l\'email:', error);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="bg-gris-clair py-8 px-4 shadow sm:rounded-lg sm:px-10">
      {successMessage ? (
        <div className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  {successMessage}
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>{t('auth.checkEmailInstructions')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Message de succès du renvoi */}
          {resendSuccess && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-800">
                    {t('auth.confirmationEmailSent')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Bouton pour renvoyer l'email */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleResendConfirmation}
              disabled={resendLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-bleu1 hover:bg-bleu3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bleu1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendLoading ? t('common.loading') : t('auth.resendConfirmation')}
            </button>

            <button
              type="button"
              onClick={() => onModeChange('login')}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gris-fonce bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bleu1"
            >
              {t('auth.login')}
            </button>
          </div>
        </div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gris-fonce">
              {t('auth.email')}
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-bleu1`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gris-fonce">
              {t('auth.password')}
            </label>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-bleu1`}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-gris-fonce/70">
                {t('auth.passwordRequirements')}
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gris-fonce">
              {t('auth.confirmPassword')}
            </label>
            <div className="mt-1">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-bleu1`}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <label className="flex items-start">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                className="mt-1 h-4 w-4 text-bleu1 focus:ring-bleu1 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gris-fonce">
                {t('auth.acceptTerms')}{' '}
                <a
                  href="/settings/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-bleu1 hover:text-bleu3 underline"
                >
                  {t('auth.termsLink')}
                </a>
              </span>
            </label>
            {errors.acceptTerms && (
              <p className="mt-1 text-sm text-red-600">{errors.acceptTerms}</p>
            )}
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-bleu1 hover:bg-bleu3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bleu1"
            >
              {loading ? t('common.loading') : t('auth.register')}
            </button>
          </div>
        </form>
      )}

      {!successMessage && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gris-fonce/80">
            {t('auth.alreadyHaveAccount')}
            <button
              type="button"
              onClick={() => onModeChange('login')}
              className="ml-1 font-medium text-bleu1 hover:text-bleu3"
            >
              {t('auth.login')}
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;